import React, { useEffect, useState } from 'react';
import { FiUser, FiCheck, FiAward, FiPlay, FiCalendar, FiDollarSign, FiHeadphones, FiTarget, FiFileText, FiClipboard, FiLogOut, FiCreditCard, FiArrowRight, FiMail, FiLock } from 'react-icons/fi';
import { signInWithGoogle, sendMagicLink, completeMagicLinkIfPresent, signOutUser, onAuth, signInWithEmailPassword, signUpWithEmailPassword, sendPasswordReset, trackEvent } from '../firebase/config.js';
import Modal from './ui/Modal.jsx';
import { trackPixels } from '../analytics/pixels.js';
import { markOnboardingNeeded } from './OnboardingWizard.jsx';

const AUTH_ERROR_MESSAGES = {
    'auth/email-already-in-use': 'That email already has an account. Try signing in instead — or use "Forgot password" if you don\'t remember it.',
    'auth/wrong-password': 'Incorrect password. Try again or use "Forgot password".',
    'auth/invalid-credential': 'Incorrect email or password. Try again or use "Forgot password".',
    'auth/user-not-found': 'No account found with that email. Check the address, or create a new account.',
    'auth/weak-password': 'Password is too weak. Use at least 6 characters.',
    'auth/invalid-email': 'That email address looks invalid.',
    'auth/too-many-requests': 'Too many attempts. Please wait a moment and try again.',
};

const getAuthErrorMessage = (err, fallback) => {
    const mapped = err?.code && AUTH_ERROR_MESSAGES[err.code];
    return mapped || err?.message || fallback;
};

const GoogleIcon = () => (
    <svg className="h-4 w-4" viewBox="0 0 48 48">
        <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12 c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24 c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"/>
        <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039 l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"/>
        <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36 c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"/>
        <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571 c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"/>
    </svg>
);

const LoginModal = ({ onClose, entitlements, onOpenManageBilling, onOpenPlanChooser, onSignupSuccess }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [status, setStatus] = useState('');
    const [user, setUser] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [authMode, setAuthMode] = useState('signin'); // 'signin' | 'signup'
    const [method, setMethod] = useState('password'); // 'password' | 'magic' (signin only)

    useEffect(() => {
        completeMagicLinkIfPresent().catch(() => {});
        const unsub = onAuth((u) => { setUser(u && !u.isAnonymous ? u : null); });
        return () => unsub && unsub();
    }, []);

    // Count a view of the login modal for onboarding funnel tracking
    useEffect(() => {
        try { trackEvent('onboarding_step', { step: 'login_modal' }); } catch (_) {}
        // Was trackPixels('Lead') — TikTok/Meta's "Lead" implies real captured intent (an
        // email submitted, a form completed). This only means the modal rendered; the user
        // may close it having done nothing. Kept as its own distinct event so it can't be
        // read as a registration count — see CompleteRegistration below for the real one.
        try { trackPixels('ViewRegistration'); } catch (_) {}
    }, []);

    const handleGoogle = async () => {
        setStatus('');
        setIsSubmitting(true);
        try {
            const result = await signInWithGoogle();
            // Treat Google sign-in as new user if no previous profile exists. Mirrors the
            // email/password path's split (sign_up+CompleteRegistration vs. plain login) —
            // previously this only fired 'login' regardless, so Google sign-ups (almost
            // certainly the majority on mobile) never registered as a conversion for the
            // TikTok/Meta pixels at all.
            if (result?.additionalUserInfo?.isNewUser) {
                try { trackEvent('sign_up', { method: 'google' }); } catch (_) {}
                try { trackPixels('CompleteRegistration'); } catch (_) {}
                markOnboardingNeeded();
                onClose();
                onSignupSuccess?.();
            } else {
                try { trackEvent('login', { method: 'google' }); } catch (_) {}
                onClose();
            }
        } catch (e) {
            const message = e?.message || 'Google sign-in failed';
            if (message.includes('missing initial state')) {
                setStatus('We could not complete Google sign-in. Please disable private browsing or allow third-party cookies, then try again.');
            } else {
                setStatus(message);
            }
        }
        finally { setIsSubmitting(false); }
    };

    const handleEmailSignIn = async (e) => {
        e && e.preventDefault();
        setStatus('');
        setIsSubmitting(true);
        try {
            if (!email) throw new Error('Please enter your email');
            if (!password) throw new Error('Please enter your password');
            await signInWithEmailPassword(email, password);
            try { trackEvent('login', { method: 'password' }); } catch (_) {}
            onClose();
        } catch (err) { setStatus(getAuthErrorMessage(err, 'Sign-in failed')); }
        finally { setIsSubmitting(false); }
    };

    const handleEmailSignUp = async (e) => {
        e && e.preventDefault();
        setStatus('');
        setIsSubmitting(true);
        try {
            if (!email) throw new Error('Please enter your email');
            if (!password) throw new Error('Please enter your password');
            await signUpWithEmailPassword(email, password);
            try { trackEvent('sign_up', { method: 'password' }); } catch (_) {}
            try { trackPixels('CompleteRegistration'); } catch (_) {}
            markOnboardingNeeded();
            onClose();
            onSignupSuccess?.();
        } catch (err) { setStatus(getAuthErrorMessage(err, 'Sign-up failed')); }
        finally { setIsSubmitting(false); }
    };

    const handleForgot = async () => {
        setStatus('');
        try { if (!email) throw new Error('Enter your email first'); await sendPasswordReset(email); setStatus('Reset email sent. Check your inbox.'); }
        catch (e) { setStatus(e.message || 'Failed to send reset email'); }
    };

    const handleEmailLink = async (e) => {
        e.preventDefault();
        setStatus('');
        try {
            if (!email) throw new Error('Please enter your email');
            await sendMagicLink(email);
            setStatus('Magic link sent. Check your email.');
        } catch (err) { setStatus(err.message || 'Failed to send magic link'); }
    };

    const handleSignOut = async () => {
        try { await signOutUser(); } catch {}
    };

    const switchMode = (mode) => {
        setAuthMode(mode);
        setMethod('password');
        setStatus('');
    };

    const inputClass = "w-full rounded-xl bg-[var(--bg-input)] border border-[var(--border)] pl-10 pr-3.5 py-2.5 text-sm text-[var(--text-1)] placeholder:text-[var(--text-3)] focus:outline-none focus:ring-2 focus:ring-teal-500/60 focus:border-teal-500/60 transition";

    const BENEFITS = [
        'Save your decks and pick up right where you left off',
        'Sync your study progress across every device',
        'Spaced-repetition mastery tracking, built in',
        'Free to start — no credit card required',
    ];

    return (
        <Modal
            title={user ? 'Account' : (authMode === 'signin' ? 'Welcome back' : 'Create your account')}
            onClose={onClose}
            size={user ? 'xl' : '4xl'}
            bodyClassName={user ? '' : '!p-0'}
        >
            {user ? (
                <div className="relative space-y-6">
                    {/* Signed in Pill */}
                    <div className="flex items-start sm:items-center gap-4">
                        <div className="relative">
                            <div className="h-12 w-12 rounded-full bg-gradient-to-br from-teal-500/40 to-emerald-500/40 p-[2px]">
                                <div className="h-full w-full rounded-full bg-slate-900/90 flex items-center justify-center">
                                    <FiUser className="h-6 w-6 text-slate-300" />
                                </div>
                            </div>
                            <span className="absolute -bottom-1 -right-1 inline-flex items-center justify-center h-5 w-5 rounded-full bg-emerald-500 shadow ring-2 ring-slate-900">
                                <FiCheck className="h-3.5 w-3.5 text-white" />
                            </span>
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                                <FiAward className="h-4 w-4 text-emerald-400" />
                                <p className="text-[15px] font-medium text-slate-100">Signed in successfully</p>
                            </div>
                            {(user.email || user.displayName) && <p className="text-sm text-slate-400 truncate">{user.email || user.displayName}</p>}
                        </div>
                    </div>

                    {/* Entitlements summary */}
                    {entitlements && (
                        <div className="rounded-xl border border-white/10 bg-slate-900/70">
                            <div className="p-4 sm:p-5">
                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-4">
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="flex items-center gap-2">
                                            <FiPlay className="h-4.5 w-4.5 text-teal-400" />
                                            <span className="text-sm text-slate-400">Plan</span>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-[15px] font-medium text-slate-100">{entitlements.tier || 'free'}</p>
                                            {onOpenPlanChooser && <button className="text-sm text-teal-300 hover:text-teal-200 underline underline-offset-2" onClick={() => onOpenPlanChooser()}>Change plan</button>}
                                        </div>
                                    </div>
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="flex items-center gap-2">
                                            <FiCalendar className="h-4.5 w-4.5 text-teal-400" />
                                            <span className="text-sm text-slate-400">Interval</span>
                                        </div>
                                        <p className="text-[15px] font-medium text-slate-100">{entitlements.interval || 'month'}</p>
                                    </div>
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="flex items-center gap-2">
                                            <FiDollarSign className="h-4.5 w-4.5 text-amber-400" />
                                            <span className="text-sm text-slate-400">Credits</span>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-[15px] font-medium text-slate-100">{typeof entitlements.creditsBalance === 'number' ? entitlements.creditsBalance : '-'}</p>
                                            {onOpenManageBilling && <button className="text-sm text-amber-300 hover:text-amber-200 underline underline-offset-2" onClick={() => onOpenManageBilling()}>Add credits</button>}
                                        </div>
                                    </div>
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="flex items-center gap-2">
                                            <FiHeadphones className="h-4.5 w-4.5 text-teal-400" />
                                            <span className="text-sm text-slate-400">Audio minutes</span>
                                        </div>
                                        <p className="text-[15px] font-medium text-slate-100">{typeof entitlements.audioMinutesRemaining === 'number' ? entitlements.audioMinutesRemaining : '-'}</p>
                                    </div>
                                </div>
                                <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 md:grid-cols-3 gap-3">
                                    <div className="flex items-center justify-between rounded-lg border border-white/10 bg-slate-900/70 px-3 py-2.5">
                                        <div className="flex items-center gap-2">
                                            <FiTarget className="h-4.5 w-4.5 text-teal-300" />
                                            <span className="text-sm text-slate-400">Games left</span>
                                        </div>
                                        <span className="text-[15px] font-medium text-slate-100">{typeof entitlements.includedGamesRemaining === 'number' ? entitlements.includedGamesRemaining : '-'}</span>
                                    </div>
                                    <div className="flex items-center justify-between rounded-lg border border-white/10 bg-slate-900/70 px-3 py-2.5">
                                        <div className="flex items-center gap-2">
                                            <FiFileText className="h-4.5 w-4.5 text-teal-300" />
                                            <span className="text-sm text-slate-400">Notes left</span>
                                        </div>
                                        <span className="text-[15px] font-medium text-slate-100">{typeof entitlements.includedNotesRemaining === 'number' ? entitlements.includedNotesRemaining : '-'}</span>
                                    </div>
                                    <div className="flex items-center justify-between rounded-lg border border-white/10 bg-slate-900/70 px-3 py-2.5">
                                        <div className="flex items-center gap-2">
                                            <FiClipboard className="h-4.5 w-4.5 text-teal-300" />
                                            <span className="text-sm text-slate-400">Exams left</span>
                                        </div>
                                        <span className="text-[15px] font-medium text-slate-100">{typeof entitlements.includedExamsRemaining === 'number' ? entitlements.includedExamsRemaining : '-'}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Quick actions */}
                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-2">
                        <button className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-white/5 hover:bg-white/10 text-slate-100 px-4 py-2.5 text-sm font-medium border border-white/10 transition" onClick={handleSignOut} disabled={isSubmitting}>
                            <FiLogOut className="h-4 w-4" />
                            Sign out
                        </button>
                        <button className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-teal-600/90 hover:bg-teal-500 text-white px-4 py-2.5 text-sm font-medium" onClick={() => onOpenManageBilling && onOpenManageBilling()}>
                            <FiCreditCard className="h-4 w-4" />
                            Manage billing
                        </button>
                        <button className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-teal-600 to-emerald-600 text-white px-4 py-2.5 text-sm font-medium" onClick={onClose}>
                            Continue
                            <FiArrowRight className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            ) : (
                <div className="flex flex-col sm:flex-row">
                    {/* Brand / benefits panel */}
                    <div className="hidden sm:flex sm:w-[260px] md:w-[300px] shrink-0 flex-col justify-between bg-gradient-to-br from-teal-600 to-emerald-700 p-7 text-white">
                        <div>
                            <p className="text-2xl font-semibold tracking-tight">
                                Flash<span className="font-light">Fonic</span>
                            </p>
                            <p className="mt-1 text-xs text-teal-100/80">Exam prep built for clinicians, by clinicians.</p>
                        </div>
                        <ul className="space-y-4">
                            {BENEFITS.map((b, idx) => (
                                <li key={idx} className="flex items-start gap-2.5 text-sm text-teal-50">
                                    <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white/15">
                                        <FiCheck className="h-3 w-3" />
                                    </span>
                                    {b}
                                </li>
                            ))}
                        </ul>
                        <p className="text-[11px] text-teal-100/60">Trusted by med students, residents, and clinicians.</p>
                    </div>

                    {/* Form panel */}
                    <div className="flex-1 min-w-0 p-6 space-y-4">
                        {/* Sign in / Create account tabs */}
                        <div className="flex items-center gap-2 rounded-xl bg-[var(--bg-input)] p-1 ring-1 ring-[var(--border)]">
                            <button type="button" className={`flex-1 rounded-lg px-3 py-1.5 text-sm font-medium transition ${authMode === 'signin' ? 'bg-gradient-to-r from-teal-600 to-emerald-600 text-white shadow' : 'text-[var(--text-2)] hover:text-[var(--text-1)]'}`} onClick={() => switchMode('signin')}>Sign in</button>
                            <button type="button" className={`flex-1 rounded-lg px-3 py-1.5 text-sm font-medium transition ${authMode === 'signup' ? 'bg-gradient-to-r from-teal-600 to-emerald-600 text-white shadow' : 'text-[var(--text-2)] hover:text-[var(--text-1)]'}`} onClick={() => switchMode('signup')}>Create account</button>
                        </div>

                        <button type="button" className="w-full inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium text-[var(--text-1)] bg-[var(--bg-input)] hover:bg-[var(--border)] border border-[var(--border)] transition" onClick={handleGoogle} disabled={isSubmitting}>
                            <GoogleIcon />
                            Continue with Google
                        </button>

                        <div className="flex items-center gap-3 py-1">
                            <div className="h-px flex-1 bg-[var(--border)]" />
                            <span className="text-[11px] uppercase tracking-wide text-[var(--text-3)]">or use email</span>
                            <div className="h-px flex-1 bg-[var(--border)]" />
                        </div>

                        {authMode === 'signin' && (
                            <div className="flex items-center gap-1 rounded-lg bg-[var(--bg-input)] p-1 ring-1 ring-[var(--border)] w-fit">
                                <button type="button" className={`rounded-md px-2.5 py-1 text-xs font-medium transition ${method === 'password' ? 'bg-[var(--bg-surface)] text-[var(--text-1)] shadow-sm' : 'text-[var(--text-3)] hover:text-[var(--text-1)]'}`} onClick={() => setMethod('password')}>Password</button>
                                <button type="button" className={`rounded-md px-2.5 py-1 text-xs font-medium transition ${method === 'magic' ? 'bg-[var(--bg-surface)] text-[var(--text-1)] shadow-sm' : 'text-[var(--text-3)] hover:text-[var(--text-1)]'}`} onClick={() => setMethod('magic')}>Magic link</button>
                            </div>
                        )}

                        {authMode === 'signup' || method === 'password' ? (
                            <form onSubmit={authMode === 'signin' ? handleEmailSignIn : handleEmailSignUp} className="space-y-3">
                                <div className="relative">
                                    <FiMail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-3)]" />
                                    <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} />
                                </div>
                                <div className="relative">
                                    <FiLock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-3)]" />
                                    <input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputClass} />
                                </div>
                                {authMode === 'signin' && (
                                    <div className="flex items-center justify-between">
                                        <button type="button" className="text-xs text-teal-500 dark:text-teal-300 hover:text-teal-600 dark:hover:text-teal-200" onClick={handleForgot}>Forgot password</button>
                                    </div>
                                )}
                                <div className="text-[11px] text-[var(--text-3)]">By continuing, you agree to FlashFonic's <a href="/terms" className="underline">Terms and Conditions</a> and <a href="/privacy" className="underline">Privacy Policy</a>.</div>
                                <button type="submit" className="w-full inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-500 hover:to-emerald-500 transition disabled:opacity-60" disabled={isSubmitting}>
                                    {authMode === 'signin' ? 'Sign in' : 'Create free account'}
                                    <FiArrowRight className="h-4 w-4" />
                                </button>
                                <p className="text-center text-xs text-[var(--text-3)]">
                                    {authMode === 'signin' ? (
                                        <>New here? <button type="button" className="text-teal-500 dark:text-teal-300 font-medium hover:underline" onClick={() => switchMode('signup')}>Create an account</button></>
                                    ) : (
                                        <>Already have an account? <button type="button" className="text-teal-500 dark:text-teal-300 font-medium hover:underline" onClick={() => switchMode('signin')}>Sign in</button></>
                                    )}
                                </p>
                            </form>
                        ) : (
                            <form onSubmit={handleEmailLink} className="space-y-3">
                                <div className="relative">
                                    <FiMail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-3)]" />
                                    <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} />
                                </div>
                                <button type="submit" className="w-full inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-500 hover:to-emerald-500 transition disabled:opacity-60" disabled={isSubmitting}>
                                    Send magic link
                                    <FiArrowRight className="h-4 w-4" />
                                </button>
                                <div className="text-[11px] text-[var(--text-3)]">Open the link on this device to complete sign-in.</div>
                            </form>
                        )}
                        {status && <div className="text-xs text-rose-500 dark:text-rose-300">{status}</div>}
                    </div>
                </div>
            )}
        </Modal>
    );
};

export default LoginModal;
